import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FileSignature, Check, X, Plus, AlertTriangle, Clock, UserCheck, Trash2,
} from 'lucide-react';
import { validazioneAPI } from '../services/validazioneApi';
import { useAuthStore } from '../store/useStore';
import '../styles/ValidazioneCampi.css';

/**
 * ValidazioneCampi — a che punto è la firma dei dieci documenti dei
 * campi clinici.
 *
 * QUESTA SCHERMATA NON TOCCA I CAMPI, e la distinzione va tenuta.
 * Qui si dice chi firma cosa e quando. I 427 campi del catalogo si
 * modificano dal Catalogo dei campi, che è un'altra schermata e un altro
 * permesso: un pannello che facesse tutte e due le cose sarebbe un
 * pannello in cui una spunta sbagliata cambia un catalogo clinico.
 *
 * I DOCUMENTI SU DISCO NON SI RISCRIVONO DA QUI. Le tabelle sono la
 * fonte; il markdown in docs/campi/ si rigenera con uno script, che è un
 * gesto deliberato di chi sviluppa. La schermata lo dice, perché
 * altrimenti chi cambia un consultato qui e poi apre il documento non
 * capisce perché non è cambiato niente.
 *
 * IL MEDICO LEGGE E BASTA. Non è la schermata a deciderlo — nascondere
 * un pulsante non è protezione: le rotte di scrittura vogliono admin o
 * segreteria. Qui si nascondono i comandi che risponderebbero 403.
 */

const ETICHETTA = {
    da_consegnare: 'Da consegnare',
    consegnato: 'Consegnato',
    firmato: 'Firmato',
    firmato_con_osservazioni: 'Firmato con osservazioni',
};

/* Il colore dice a che punto è, non quanto è grave: «da consegnare» non
   è un problema, è l'inizio. Il giallo compare quando qualcosa è fuori
   da qualcuno e si aspetta. */
const COLORE = {
    da_consegnare: 'neutro',
    consegnato: 'attesa',
    firmato: 'buono',
    firmato_con_osservazioni: 'buono',
};

const oggi = () => new Date().toLocaleDateString('sv-SE');   // AAAA-MM-GG locale

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const iso = (v) => (v ? String(v).slice(0, 10) : '');

const messaggio = (e, ripiego) => e?.response?.data?.message || e?.message || ripiego;

const ValidazioneCampi = () => {
    const utente = useAuthStore((s) => s.user);
    const puoScrivere = utente?.ruolo === 'admin' || utente?.ruolo === 'segreteria';

    const [documenti, setDocumenti] = useState([]);
    const [riepilogo, setRiepilogo] = useState(null);
    const [medici, setMedici] = useState([]);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [aperto, setAperto] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    const [daAggiungere, setDaAggiungere] = useState({});

    const carica = useCallback(async () => {
        try {
            const r = await validazioneAPI.elenco();
            setDocumenti(r.data.data.documenti || []);
            setRiepilogo(r.data.data.riepilogo);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a caricare i documenti'));
        }
    }, []);

    useEffect(() => { carica(); }, [carica]);
    useEffect(() => {
        if (!puoScrivere) return;
        validazioneAPI.medici().then((r) => setMedici(r.data.data.medici || [])).catch(() => {});
    }, [puoScrivere]);

    const salva = async (id, dati, testo) => {
        try {
            setErrore(null);
            setInCorso(true);
            await validazioneAPI.aggiorna(id, dati);
            setAvviso(testo || 'Salvato.');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    /* Cambiare stato porta con sé la data, e la propone: chiedere di
       scriverla a mano ogni volta è il modo di ritrovarsi con metà delle
       date vuote e il conto «da quanto è fuori» che non funziona più. */
    const cambiaStato = (d, stato) => {
        const dati = { stato };
        if (stato === 'da_consegnare') {
            dati.consegnato_il = null;
            dati.firmato_il = null;
        } else {
            dati.consegnato_il = iso(d.consegnato_il) || oggi();
            dati.firmato_il = (stato === 'firmato' || stato === 'firmato_con_osservazioni')
                ? (iso(d.firmato_il) || oggi()) : null;
        }
        return salva(d.id, dati, `${d.titolo}: ${ETICHETTA[stato].toLowerCase()}.`);
    };

    const aggiungi = async (d) => {
        const medicoId = daAggiungere[d.id];
        if (!medicoId) return;
        try {
            setErrore(null);
            await validazioneAPI.aggiungiConsultato(d.id, medicoId);
            setDaAggiungere({ ...daAggiungere, [d.id]: '' });
            setAvviso('Consultato aggiunto. Per vederlo nel documento serve rigenerarlo.');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'aggiunta'));
        }
    };

    const togli = async (c) => {
        if (!window.confirm(`Togliere ${c.nominativo} dai consultati?`)) return;
        try {
            setErrore(null);
            await validazioneAPI.togliConsultato(c.id);
            setAvviso('Tolto dai consultati.');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella rimozione'));
        }
    };

    const visto = async (c, valore) => {
        try {
            setErrore(null);
            await validazioneAPI.vistoConsultato(c.id, valore || null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        }
    };

    /* Chi è già fra i consultati non ricompare nella tendina: sceglierlo
       darebbe un errore che si poteva evitare prima del clic. */
    const disponibili = useMemo(() => {
        const per = {};
        documenti.forEach((d) => {
            const dentro = new Set(d.consultati.map((c) => c.medico_id).filter(Boolean));
            per[d.id] = medici.filter((m) => m.id !== d.responsabile_id && !dentro.has(m.id));
        });
        return per;
    }, [documenti, medici]);

    return (
        <div className="vcp-pagina">
            <header className="vcp-testa">
                <div>
                    <h1><FileSignature size={22} aria-hidden="true" /> Validazione dei campi clinici</h1>
                    <p>
                        Dieci documenti descrivono i campi che le schede usano. Ognuno va
                        letto da un medico, che firma <b>nome, tipo, unità di misura e
                        formula</b> — non gli intervalli di riferimento, che nei documenti
                        non ci sono.
                    </p>
                </div>
            </header>

            {/* Il conto che serve davvero: quanti sono fuori e da quanto.
                Dieci righe si contano a occhio; «fuori da 34 giorni» no, ed
                è quello che dice se il giro si è fermato. */}
            {riepilogo && (
                <div className="vcp-riepilogo">
                    <div className={riepilogo.fuori ? 'attesa' : 'buono'}>
                        <b>{riepilogo.fuori}</b>
                        <span>{riepilogo.fuori === 1 ? 'documento non firmato' : 'documenti non firmati'}</span>
                    </div>
                    <div>
                        <b>{riepilogo.da_consegnare}</b>
                        <span>ancora da consegnare</span>
                    </div>
                    <div>
                        <b>{riepilogo.consegnati}</b>
                        <span>consegnati, in attesa di firma</span>
                    </div>
                    <div className={riepilogo.attesa_massima > 30 ? 'attesa' : ''}>
                        <b>{riepilogo.attesa_massima != null ? `${riepilogo.attesa_massima} gg` : '—'}</b>
                        <span>
                            {riepilogo.piu_vecchio
                                ? `il più vecchio: ${riepilogo.piu_vecchio}`
                                : 'nessuno in attesa'}
                        </span>
                    </div>
                </div>
            )}

            {errore && <p className="vcp-errore"><AlertTriangle size={15} /> {errore}
                <button onClick={() => setErrore(null)}><X size={14} /></button></p>}
            {avviso && <p className="vcp-avviso"><Check size={15} /> {avviso}
                <button onClick={() => setAvviso(null)}><X size={14} /></button></p>}

            {!puoScrivere && (
                <p className="vcp-nota">
                    Stai guardando l&apos;assegnazione in sola lettura: si modifica
                    dall&apos;amministrazione o dalla segreteria.
                </p>
            )}

            <div className="vcp-elenco">
                {documenti.map((d) => {
                    const apertoQui = aperto === d.id;
                    return (
                        <article key={d.id} className="vcp-doc">
                            <button className="vcp-doc-testa"
                                    onClick={() => setAperto(apertoQui ? null : d.id)}>
                                <span className="vcp-titolo">{d.titolo}</span>
                                <span className={`vcp-pillola ${COLORE[d.stato]}`}>
                                    {ETICHETTA[d.stato]}
                                </span>
                                {d.giorni_fuori != null && (
                                    <span className="vcp-giorni" title="Consegnato e non ancora firmato">
                                        <Clock size={13} /> {d.giorni_fuori} gg
                                    </span>
                                )}
                                <span className="vcp-resp">{d.responsabile}</span>
                                <span className="vcp-conta">
                                    {d.consultati.length
                                        ? `${d.consultati.length} consultati` : 'nessun consultato'}
                                </span>
                                {/* I campi che il documento descrive. Senza
                                    questo numero la schermata dice a che punto
                                    è la carta e tace su ciò che la carta
                                    descrive. */}
                                <span className="vcp-conta" title="Campi dell'area che portano la conferma di un medico">
                                    {d.campi_confermati}/{d.campi} campi
                                    {d.campi_bozza > 0 && ` · ${d.campi_bozza} in bozza`}
                                </span>
                            </button>

                            {apertoQui && (
                                <div className="vcp-corpo">
                                    <div className="vcp-riga">
                                        <label className="vcp-campo">
                                            <span>Responsabile</span>
                                            {puoScrivere ? (
                                                <select value={d.responsabile_id}
                                                        onChange={(e) => salva(d.id,
                                                            { responsabile_id: Number(e.target.value) },
                                                            'Responsabile cambiato.')}>
                                                    {medici.map((m) => (
                                                        <option key={m.id} value={m.id}>
                                                            {m.cognome} {m.nome}
                                                            {m.specializzazione ? ` — ${m.specializzazione}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : <p className="vcp-valore">{d.responsabile}</p>}
                                        </label>

                                        <label className="vcp-campo">
                                            <span>Consegnato il</span>
                                            {puoScrivere ? (
                                                <input type="date" value={iso(d.consegnato_il)}
                                                       disabled={d.stato === 'da_consegnare'}
                                                       onChange={(e) => salva(d.id,
                                                           { consegnato_il: e.target.value })} />
                                            ) : <p className="vcp-valore">{dataIta(d.consegnato_il)}</p>}
                                        </label>

                                        <label className="vcp-campo">
                                            <span>Firmato il</span>
                                            {puoScrivere ? (
                                                <input type="date" value={iso(d.firmato_il)}
                                                       disabled={!d.firmato_il}
                                                       onChange={(e) => salva(d.id,
                                                           { firmato_il: e.target.value })} />
                                            ) : <p className="vcp-valore">{dataIta(d.firmato_il)}</p>}
                                        </label>
                                    </div>

                                    {puoScrivere && d.campi > 0 && (
                                        <p className="vcp-effetto">
                                            Portandolo a <b>firmato</b>, i <b>{d.campi} campi</b> di
                                            quest&apos;area prendono il nome del responsabile e la data.
                                            {d.campi_bozza > 0
                                                ? ` I ${d.campi_bozza} in bozza diventano attivi.`
                                                : ' Nessun campo in bozza da attivare.'}
                                            {' '}I campi attivi <b>lo erano già prima</b>: la firma è un
                                            controllo su ciò che gira, non un permesso di partire.
                                        </p>
                                    )}
                                    {puoScrivere && (
                                        <div className="vcp-stati">
                                            <span>Stato</span>
                                            {Object.entries(ETICHETTA).map(([k, e]) => (
                                                <button key={k} disabled={inCorso || d.stato === k}
                                                        className={`vcp-stato ${d.stato === k ? 'scelto' : ''}`}
                                                        onClick={() => cambiaStato(d, k)}>
                                                    {e}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="vcp-consultati">
                                        <h4>Consultati</h4>
                                        <p className="vcp-sotto">
                                            Leggono il documento e portano le osservazioni al
                                            responsabile. <b>Non firmano</b>: la firma è una sola.
                                        </p>
                                        {d.consultati.length === 0 ? (
                                            <p className="vcp-vuoto">
                                                Nessuno. Questo documento riguarda una branca seguita
                                                da un medico solo.
                                            </p>
                                        ) : (
                                            <table className="vcp-tabella">
                                                <thead>
                                                    <tr><th>Nome</th><th>Visto il</th><th /></tr>
                                                </thead>
                                                <tbody>
                                                    {d.consultati.map((c) => (
                                                        <tr key={c.id}>
                                                            <td>
                                                                {c.nominativo}
                                                                {!c.in_anagrafica && (
                                                                    <span className="vcp-fuori"
                                                                          title="Non risulta fra i medici in anagrafica: è stato lasciato come indicato, va controllato prima di far firmare">
                                                                        non in anagrafica
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {puoScrivere ? (
                                                                    <input type="date" value={iso(c.visto_il)}
                                                                           onChange={(e) => visto(c, e.target.value)} />
                                                                ) : dataIta(c.visto_il)}
                                                            </td>
                                                            <td className="vcp-azioni">
                                                                {puoScrivere && !c.visto_il && (
                                                                    <button className="vcp-icona"
                                                                            title="Segna come visto oggi"
                                                                            onClick={() => visto(c, oggi())}>
                                                                        <UserCheck size={15} />
                                                                    </button>
                                                                )}
                                                                {puoScrivere && (
                                                                    <button className="vcp-icona"
                                                                            title="Togli dai consultati"
                                                                            onClick={() => togli(c)}>
                                                                        <Trash2 size={15} />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}

                                        {puoScrivere && (
                                            <div className="vcp-aggiungi">
                                                <select value={daAggiungere[d.id] || ''}
                                                        onChange={(e) => setDaAggiungere({
                                                            ...daAggiungere, [d.id]: e.target.value })}>
                                                    <option value="">Aggiungi un consultato…</option>
                                                    {(disponibili[d.id] || []).map((m) => (
                                                        <option key={m.id} value={m.id}>
                                                            {m.cognome} {m.nome}
                                                            {m.specializzazione ? ` — ${m.specializzazione}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button className="btn-secondario"
                                                        disabled={!daAggiungere[d.id]}
                                                        onClick={() => aggiungi(d)}>
                                                    <Plus size={15} /> Aggiungi
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <label className="vcp-campo vcp-note">
                                        <span>Osservazioni del responsabile</span>
                                        {puoScrivere ? (
                                            <textarea rows={3} defaultValue={d.note || ''}
                                                      placeholder="Un campo da togliere, un'unità sbagliata, una voce d'elenco che in clinica si chiama in un altro modo"
                                                      onBlur={(e) => {
                                                          if ((e.target.value || '') !== (d.note || '')) {
                                                              salva(d.id, { note: e.target.value });
                                                          }
                                                      }} />
                                        ) : <p className="vcp-valore">{d.note || '—'}</p>}
                                    </label>
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>

            {puoScrivere && (
                <p className="vcp-nota">
                    <b>I documenti su disco non cambiano da qui.</b> Queste sono le tabelle
                    da cui i documenti vengono generati: il testo in coda ai dieci file e la
                    pagina delle assegnazioni si riscrivono con lo script di rigenerazione,
                    che lancia chi sviluppa. È voluto: uno script che riscrive i file a ogni
                    salvataggio, il giorno in cui qualcuno ci sta lavorando sopra, glieli
                    sovrascrive sotto le mani.
                </p>
            )}
        </div>
    );
};

export default ValidazioneCampi;
